import { motion } from 'framer-motion';
import { Camera, Plus, Check, Loader2, Star, Scan, Heart } from 'lucide-react';
import { Button } from '../ui/button';

export default function SportPhotoCard({
  photo,
  index = 0,
  inCart = false,
  isAdding = false,
  isFavorite = false,
  onToggleFavorite,
  pricing = null,
  onAddToCart,
  onClick,
  viewMode = 'grid',
  t = {},
}) {
  const hasAI = photo.ai_tags && photo.ai_tags.length > 0;
  const stars = photo.star_rating || 0;

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.015 }}
        className="flex items-center gap-3 p-2 rounded-lg bg-secondary/20 hover:bg-secondary/40 transition-colors group cursor-pointer"
        onClick={onClick}
      >
        <div className="w-16 h-16 flex-shrink-0 rounded overflow-hidden bg-secondary">
          {photo.variants?.thumbnail ? (
            <img
              src={photo.variants.thumbnail}
              alt={photo.filename}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono truncate">{photo.filename}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {stars > 0 && (
              <span className="text-xs text-yellow-500 flex items-center gap-0.5">
                <Star className="w-3 h-3 fill-current" /> {stars}
              </span>
            )}
            {hasAI && (
              <span className="text-xs text-blue-400 flex items-center gap-0.5">
                <Scan className="w-3 h-3" /> AI
              </span>
            )}
            {photo.variants?.width > 0 && (
              <span className="text-xs text-muted-foreground">
                {photo.variants.width}x{photo.variants.height}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {onToggleFavorite && (
            <button
              onClick={() => onToggleFavorite(photo.id)}
              className={`p-1.5 rounded transition-colors ${isFavorite ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
            </button>
          )}
          {inCart ? (
            <Button size="sm" variant="secondary" className="text-xs h-7" disabled>
              <Check className="w-3 h-3 mr-1" /> {t.inCart || 'In cart'}
            </Button>
          ) : (
            <Button
              size="sm"
              className="text-xs h-7"
              onClick={() => onAddToCart?.(photo.id)}
              disabled={isAdding}
            >
              {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
              {pricing ? `${pricing.per_photo_price} ${pricing.currency}` : (t.addToCart || 'Add')}
            </Button>
          )}
        </div>
      </motion.div>
    );
  }

  // Grid and masonry modes
  const isMasonry = viewMode === 'masonry';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.02 }}
      className={`bg-secondary rounded-lg overflow-hidden group cursor-pointer ${
        isMasonry ? 'break-inside-avoid' : ''
      }`}
      onClick={onClick}
    >
      {/* Image container */}
      <div className={`relative ${isMasonry ? '' : 'aspect-square'} overflow-hidden`}>
        {photo.variants?.thumbnail ? (
          <img
            src={isMasonry ? (photo.variants.preview || photo.variants.thumbnail) : photo.variants.thumbnail}
            alt={photo.filename}
            className={`w-full ${isMasonry ? 'h-auto' : 'h-full object-cover'} group-hover:scale-105 transition-transform duration-300`}
            loading="lazy"
          />
        ) : (
          <div className={`w-full ${isMasonry ? 'h-48' : 'h-full'} flex items-center justify-center`}>
            <Camera className="w-8 h-8 text-muted-foreground" />
          </div>
        )}

        {/* Favorite button */}
        {onToggleFavorite && (
          <button
            className={`absolute top-2 right-2 p-1.5 rounded-full bg-black/40 transition-colors z-10 ${
              isFavorite ? 'text-red-500' : 'text-white/70 opacity-0 group-hover:opacity-100 hover:text-red-400'
            } ${isFavorite ? 'opacity-100' : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(photo.id); }}
          >
            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        )}

        {/* Rating badge */}
        {stars > 0 && (
          <div className={`absolute ${onToggleFavorite ? 'top-10' : 'top-2'} right-2 bg-black/50 text-yellow-400 text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5`}>
            <Star className="w-3 h-3 fill-current" /> {stars}
          </div>
        )}

        {/* AI badge */}
        {hasAI && (
          <div className="absolute top-2 left-2 bg-blue-500/70 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5">
            <Scan className="w-3 h-3" /> {photo.ai_tags.length}
          </div>
        )}

        {/* Cart overlay */}
        <div
          className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={e => e.stopPropagation()}
        >
          {inCart ? (
            <Button size="sm" variant="secondary" className="w-full text-xs h-7" disabled>
              <Check className="w-3 h-3 mr-1" /> {t.inCart || 'In cart'}
            </Button>
          ) : (
            <Button
              size="sm"
              className="w-full text-xs h-7"
              onClick={() => onAddToCart?.(photo.id)}
              disabled={isAdding}
            >
              {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
              {pricing ? `${pricing.per_photo_price} ${pricing.currency}` : (t.addToCart || 'Add')}
            </Button>
          )}
        </div>
      </div>

      {/* Info section below image */}
      <div className="px-2 py-1.5">
        <p className="text-[11px] font-mono text-muted-foreground truncate">{photo.filename}</p>
        {photo.photographer_name && (
          <p className="text-[10px] text-muted-foreground/70 truncate">{photo.photographer_name}</p>
        )}
      </div>
    </motion.div>
  );
}
